/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'

export const _2 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>2 - События</h2>
    <p className={styles.description}>
      Для нативного события браузера в React создается объект-обертка{' '}
      <code className={styles.code}>SyntheticEvent Object</code> с идентичным интерфейсом. Это необходимо чтобы
      предоставить кросс-бразуерность и оптимизировать производительность.
    </p>
    <CopyBlock
      text={`<button onClick={event => console.log(event)}>Click me!</button>`}
      language="javascript"
      showLineNumbers={false}
      theme={ocean}
    />
    <ul className={styles.list}>
      <li className={styles.list__item}>
        Добавление обработчика событий с <code className={styles.code}>EventTarget.addEventListener()</code> почти не
        используется, за редким исключением.
      </li>
      <li className={styles.list__item}>
        Пропсы событий не исключение и именуются с помощью camelCase. Например{' '}
        <code className={styles.code}>onClick</code>, <code className={styles.code}>onChange</code>,{' '}
        <code className={styles.code}>onSubmit</code>, <code className={styles.code}>onMouseEnter</code>.
      </li>
      <li className={styles.list__item}>
        В проп события передается ссылка на callback-функцию, которая будет вызвана при наступлении события.
      </li>
      <li className={styles.list__item}>
        Обработчики событий получают экземпляр <code className={styles.code}>SyntheticEvent Object</code>.
      </li>
    </ul>
    <p className={styles.description}>
      В React реализовано глобальное делегирование событий. Слушатели не добавляются к DOM-элементам напрямую. React
      использует один обработчик событий на корне документа, который отвечает за прослушивание всех событий и при
      необходимости вызывает соответствующий обработчик.{' '}
    </p>
    <p className={styles.notificationWarn}>
      Именно поэтому объект <code className={styles.code}>SyntheticEvent</code> всего один (на все приложение) и
      доступен только в синхронном коде. Сразу после вызова callback-функции он будет использован повторно и все
      свойства будут аннулированы.{' '}
    </p>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.1 - Счетчик</h3>
      <p className={styles.description}>Создадим компонент-счетчик с возможностью увеличения и уменьшения значения. </p>
      <CopyBlock
        text={`import React, { Component } from 'react';
import ReactDOM from 'react-dom';

class Counter extends Component {
  static defaultProps = {
    step: 1,
  };

  render() {
    const { step } = this.props;

    return (
      <div>
        <span>0</span>
        <button type="button">Increment by {step}</button>
        <button type="button">Decrement by {step}</button>
      </div>
    );
  }
}

ReactDOM.render(<Counter step={5} />, document.getElementById('root'));`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.2 - Анонимные колбеки</h3>
      <p className={styles.description}>
        Инлайн колбеки считаются антипаттерном. Каждый раз когда компонент ре-рендерится, будет создана новая
        callback-функция. В многих случаях это нормально. Но, если callback передается как проп нижележащим компонентам
        в дереве, они будут отрендерены заново, так как придут новые пропы ссылочного типа (функция).
      </p>
      <CopyBlock
        text={`class Counter extends Component {
  /* ... */

  render() {
    const { step } = this.props;

    return (
      <div>
        <span>0</span>
        <button
          type="button"
          onClick={evt => {
            console.log('Increment button was clicked!', evt); // работает
            console.log('this.props: ', this.props); // работает
          }}
        >
          > Increment by {step}
        </button>
        <button
          type="button"
          onClick={evt => {
            console.log('Decrement button was clicked!', evt); // работает
            console.log('this.props: ', this.props); // работает
          }}
        >
          Decrement by {step}
        </button>
      </div>
    );
  }
}`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.3 - Кастомные методы</h3>
      <p className={styles.description}>
        Чаще всего обработчики событий объявляются как методы класса, после чего jsx-атрибуту передается ссылка на
        метод.{' '}
      </p>
      <CopyBlock
        text={`class Counter extends Component {
  /* ... */

  handleIncrement(evt) {
    console.log('Increment button was clicked!', evt); // работает
    console.log('this.props: ', this.props); // Error: cannot read props of undefined
  }

  handleDecrement(evt) {
    console.log('Decrement button was clicked!', evt); // работает
    console.log('this.props: ', this.props); // Error: cannot read props of undefined
  }

  render() {
    const { step } = this.props;

    return (
      <div>
        <span>0</span>
        <button type="button" onClick={this.handleIncrement}>
          Increment by {step}
        </button>
        <button type="button" onClick={this.handleDecrement}>
          Decrement by {step}
        </button>
      </div>
    );
  }
}`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.4 - Привязка контекста</h3>
      <p className={styles.description}>
        Нужно всегда помнить о значении <code className={styles.code}>this</code> в методах использующихся как
        callback-функции. В JavaScript, контекст в методах класса не привязывается по умолчанию. Если забыть привязать
        контекст, и передать метод как callback-функцию обработчику события, во время вызова функции,{' '}
        <code className={styles.code}>this</code> будет неопределен (<code className={styles.code}>undefined</code>).{' '}
      </p>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>2.4.1 - Привязка контекста</h4>
        <p className={styles.description}>
          Избегайте привязки контекста в методе <code className={styles.code}>render()</code>. Всякий раз, когда
          компонент ре-рендерится, <code className={styles.code}>Function.prototype.bind()</code> возвращает новую
          функцию и передает ее вниз по дереву компонентов, что приводит к повторному рендеру дочерних компонентов. При
          достаточном количестве, это оказывает существенное влияние на производительность.{' '}
        </p>
        <CopyBlock
          text={`// ❌ Плохо
class Counter extends Component {
  /* ... */

  handleIncrement(evt) {
    // ...
  }

  handleDecrement(evt) {
    // ...
  }

  render() {
    const { step } = this.props;

    return (
      <div>
        <span>0</span>
        <button type="button" onClick={this.handleIncrement.bind(this)}>
          Increment by {step}
        </button>
        <button type="button" onClick={this.handleDecrement.bind(this)}>
          Decrement by {step}
        </button>
      </div>
    );
  }
}`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>2.4.2 - Привязка в конструкторе</h4>
        <p className={styles.description}>
          Еще один способ привязать контекст - сделать это в конструкторе класса. Если callback-функций много, можете
          себе представить, насколько большой может получиться конструктор.{' '}
        </p>
        <ul className={styles.list}>
          <li className={styles.list__item}>
            Конструктор выполняется один раз, поэтому <code className={styles.code}>bind</code> вызовется один раз
          </li>
          <li className={styles.list__item}>Методы класса записываеются в свойство prototype функции-конструктора</li>
        </ul>
        <CopyBlock
          text={`// ✅ Хорошо
class Counter extends Component {
  /* ... */

  constructor() {
    super();

    this.handleIncrement = this.handleIncrement.bind(this);
    this.handleDecrement = this.handleDecrement.bind(this);
  }

  handleIncrement(evt) {
    // ...
  }

  handleDecrement(evt) {
    // ...
  }

  render() {
    const { step } = this.props;

    return (
      <div>
        <span>0</span>
        <button type="button" onClick={this.handleIncrement}>
          Increment by {step}
        </button>
        <button type="button" onClick={this.handleDecrement}>
          Decrement by {step}
        </button>
      </div>
    );
  }
}`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>2.4.3 - Публичные свойства класса</h4>
        <p className={styles.description}>
          Несмотря на то, что это рекомендуемый способ привязки контекста, синтаксис публичных полей класса еще не
          стандартизирован. Но они уже настолько широко используются, что даже если будут синтаксические изменения,
          транспайлер Babel все сделает за нас.{' '}
        </p>
        <p className={styles.description}>
          При объявлении публичных полей класса, они записываются не в свойство{' '}
          <code className={styles.code}>prototype</code> функции-конструктора, а в объект экземпляра.{' '}
        </p>
        <CopyBlock
          text={`// ✅ Хорошо
class Counter extends Component {
  /* ... */

  handleIncrement = evt => {
    console.log('Increment button was clicked!', evt); // работает
    console.log('this.props: ', this.props); // работает
  };

  handleDecrement = evt => {
    console.log('Decrement button was clicked!', evt); // работает
    console.log('this.props: ', this.props); // работает
  };

  render() {
    const { step } = this.props;

    return (
      <div>
        <span>0</span>
        <button type="button" onClick={this.handleIncrement}>
          Increment by {step}
        </button>
        <button type="button" onClick={this.handleDecrement}>
          Decrement by {step}
        </button>
      </div>
    );
  }
}
`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.5 - Дополнительные материалы</h3>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            href="https://ru.reactjs.org/docs/events.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            Документация SyntheticEvent
          </a>
        </li>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            href="https://codeburst.io/pointer-events-with-react-the-why-how-what-617a5b51dbb2"
            target="_blank"
            rel="noopener noreferrer"
          >
            Pointer events with React
          </a>
        </li>
      </ul>
    </section>
  </section>
))
